import * as React from "react"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"
import { type DateRange } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

type DateRangePickerProps = {
  id?: string
  startDate?: string
  endDate?: string
  onChange: (range: { startDate?: string; endDate?: string }) => void
  disabled?: boolean
  placeholder?: string
  className?: string
}

const parseIsoDate = (value?: string) => {
  if (!value) return undefined
  const date = new Date(`${value}T00:00:00`)
  return Number.isNaN(date.getTime()) ? undefined : date
}

const toIsoDate = (date?: Date) => {
  if (!date) return undefined
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export function DateRangePicker({
  id,
  startDate,
  endDate,
  onChange,
  disabled,
  placeholder = "Pick a date range",
  className,
}: DateRangePickerProps) {
  const from = React.useMemo(() => parseIsoDate(startDate), [startDate])
  const to = React.useMemo(() => parseIsoDate(endDate), [endDate])

  const selectedRange = React.useMemo<DateRange | undefined>(() => {
    if (!from && !to) return undefined
    return { from, to }
  }, [from, to])

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !from && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {from ? (
            to ? (
              `${format(from, "PPP")} - ${format(to, "PPP")}`
            ) : (
              format(from, "PPP")
            )
          ) : (
            placeholder
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          captionLayout="dropdown"
          startMonth={new Date(new Date().getFullYear() - 10, 0, 1)}
          endMonth={new Date(new Date().getFullYear() + 5, 11, 1)}
          numberOfMonths={2}
          selected={selectedRange}
          onSelect={(range) =>
            onChange({
              startDate: toIsoDate(range?.from),
              endDate: toIsoDate(range?.to),
            })
          }
        />
      </PopoverContent>
    </Popover>
  )
}
